import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tag, Plus, MapPin, Upload, Loader2, Trash2, Pencil, ImageIcon, User, MessageSquare, Send, Inbox } from "lucide-react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { SaleListing, Property, PropertyImage, SaleListingStatus } from "@shared/schema";
import { sortProperties } from "@/lib/property-utils";
import { BuyerInquiriesView } from "@/components/buyer-inquiries-view";
import { usePageTitle } from "@/hooks/use-page-title";

type MarketplaceListing = SaleListing & {
  propertyName: string;
  propertyAddress: string | null;
  latitude: number | null;
  longitude: number | null;
  sellerName: string | null;
};

type InquirySummary = {
  id: number;
  listingId: number;
  sellerId: number;
  buyerId: number;
  lastMessagePreview: string | null;
  lastMessageAt: string;
  unreadCount: number;
  listingPropertyName: string;
  listingPropertyAddress: string | null;
  listingAskingPrice: string;
  listingPhotos: string[];
  listingStatus: string;
  sellerDisplayName: string | null;
  buyerDisplayName: string | null;
};

const STATUS_LABEL: Record<SaleListingStatus, string> = {
  active: "Active",
  under_contract: "Under Contract",
  sold: "Sold",
};

const STATUS_COLOR: Record<SaleListingStatus, string> = {
  active: "bg-green-100 text-green-800 border-green-200",
  under_contract: "bg-amber-100 text-amber-800 border-amber-200",
  sold: "bg-gray-100 text-gray-700 border-gray-200",
};

const AUTO_SELECT_LIMIT = 10;

function formatCurrency(amount: number | string | null | undefined): string {
  if (amount == null || amount === "") return "—";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (Number.isNaN(num)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
}

async function uploadOneFile(file: File): Promise<string> {
  const reader = new FileReader();
  const dataUrl: string = await new Promise((resolve, reject) => {
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });
  const base64 = dataUrl.split(",")[1];
  const res = await apiRequest("POST", "/api/app/upload", {
    filename: file.name,
    mimeType: file.type,
    data: base64,
  });
  const data = await res.json();
  return data.imageUrl as string;
}

function PhotoUploadButton({
  onUploaded,
  testId,
  onUploadingChange,
}: {
  onUploaded: (urls: string[]) => void;
  testId: string;
  onUploadingChange?: (uploading: boolean) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);
  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map((f) => uploadOneFile(f)));
      onUploaded(urls);
    } catch (err: any) {
      toast({ title: "Upload failed", description: parseErrorMessage(err, "Could not upload photos"), variant: "destructive" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-gray-300 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer" data-testid={`${testId}-browse`}>
      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      {uploading ? "Uploading..." : "Upload photos"}
      <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} disabled={uploading} />
    </label>
  );
}

type FormState = {
  propertyId: string;
  askingPrice: string;
  description: string;
  photos: string[];
  status: SaleListingStatus;
  sellerName: string;
};

const EMPTY_FORM: FormState = {
  propertyId: "",
  askingPrice: "",
  description: "",
  photos: [],
  status: "active",
  sellerName: "",
};

function ListingForm({
  initial, properties, mode, onSubmit, onCancel, submitting, submitLabel, onUploadingChange,
}: {
  initial: FormState;
  properties: Property[];
  mode: "create" | "edit";
  onSubmit: (data: FormState) => void;
  onCancel: () => void;
  submitting: boolean;
  submitLabel: string;
  onUploadingChange?: (uploading: boolean) => void;
}) {
  const [form, setForm] = useState<FormState>(initial);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [extraPhotos, setExtraPhotos] = useState<string[]>(() =>
    initial.photos.filter(() => mode === "edit"),
  );

  const handleUploadingChange = (next: boolean) => {
    setUploading(next);
    onUploadingChange?.(next);
  };

  const propertyId = form.propertyId ? parseInt(form.propertyId, 10) : null;
  const { data: propertyImages } = useQuery<PropertyImage[]>({
    queryKey: ["/api/app/properties", propertyId, "images"],
    enabled: propertyId != null,
  });

  const propertyImageUrls = useMemo(() => (propertyImages || []).map((img) => img.imageUrl), [propertyImages]);

  useEffect(() => {
    if (mode !== "create") return;
    if (!propertyId || propertyImageUrls.length === 0) return;
    setForm((prev) => {
      if (prev.photos.length > 0) return prev;
      return { ...prev, photos: propertyImageUrls.slice(0, AUTO_SELECT_LIMIT) };
    });
  }, [propertyId, propertyImageUrls, mode]);

  const togglePhoto = (url: string) => {
    setForm((prev) => {
      const exists = prev.photos.includes(url);
      return { ...prev, photos: exists ? prev.photos.filter((p) => p !== url) : [...prev.photos, url] };
    });
  };

  const removeExtra = (url: string) => {
    setExtraPhotos((prev) => prev.filter((p) => p !== url));
    setForm((prev) => ({ ...prev, photos: prev.photos.filter((p) => p !== url) }));
  };

  const addExtraUploaded = (urls: string[]) => {
    setExtraPhotos((prev) => [...prev, ...urls]);
    setForm((prev) => ({ ...prev, photos: [...prev.photos, ...urls] }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.propertyId) { setError("Please select a property"); return; }
    const price = parseFloat(form.askingPrice);
    if (!price || price <= 0) { setError("Asking price must be greater than 0"); return; }
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="propertyId">Property</Label>
        <Select value={form.propertyId} onValueChange={(v) => setForm({ ...form, propertyId: v, photos: [] })}>
          <SelectTrigger id="propertyId" data-testid="select-listing-property">
            <SelectValue placeholder="Select a property" />
          </SelectTrigger>
          <SelectContent>
            {properties.length === 0 && (
              <SelectItem value="none" disabled>No active properties available</SelectItem>
            )}
            {sortProperties(properties).map((p) => (
              <SelectItem key={p.id} value={String(p.id)} data-testid={`option-property-${p.id}`}>
                {p.name}{p.address ? ` — ${p.address}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 mt-1">Only properties with status "Active" can be listed for sale.</p>
      </div>

      <div>
        <Label htmlFor="askingPrice">Asking Price (USD)</Label>
        <Input
          id="askingPrice"
          type="number"
          min="0"
          step="1"
          value={form.askingPrice}
          onChange={(e) => setForm({ ...form, askingPrice: e.target.value })}
          placeholder="350000"
          data-testid="input-asking-price"
        />
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          rows={4}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Tell buyers about the property, recent renovations, current revenue, etc."
          data-testid="input-description"
        />
      </div>

      <div>
        <Label>Photos</Label>
        {!propertyId ? (
          <p className="text-sm text-gray-500">Select a property to choose from its existing photos.</p>
        ) : propertyImageUrls.length === 0 && extraPhotos.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="text-no-property-photos">
            This property has no photos yet. Upload some below to attach to your listing.
          </p>
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-2">
              The first {AUTO_SELECT_LIMIT} photos from this property are auto-selected. Uncheck any you don't want, or check additional photos.
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {propertyImageUrls.map((url, i) => {
                const checked = form.photos.includes(url);
                return (
                  <label
                    key={url}
                    className={`relative block w-full aspect-square rounded border overflow-hidden cursor-pointer ${checked ? "ring-2 ring-blue-500" : ""}`}
                    data-testid={`property-photo-${i}`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <div className="absolute top-1 left-1 bg-white/90 rounded p-0.5">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => togglePhoto(url)}
                        data-testid={`checkbox-photo-${i}`}
                      />
                    </div>
                  </label>
                );
              })}
              {extraPhotos.map((url, i) => {
                const checked = form.photos.includes(url);
                return (
                  <div
                    key={url}
                    className={`relative block w-full aspect-square rounded border overflow-hidden ${checked ? "ring-2 ring-blue-500" : ""}`}
                    data-testid={`extra-photo-${i}`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <div className="absolute top-1 left-1 bg-white/90 rounded p-0.5">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => togglePhoto(url)}
                        data-testid={`checkbox-extra-${i}`}
                      />
                    </div>
                    <button
                      type="button"
                      className="absolute top-1 right-1 bg-gray-700 text-white text-xs w-5 h-5 rounded"
                      onClick={() => removeExtra(url)}
                      data-testid={`remove-extra-${i}`}
                      aria-label="Remove uploaded photo"
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {propertyId && (
          <div className="mt-2">
            <PhotoUploadButton onUploaded={addExtraUploaded} testId="listing-photos" onUploadingChange={handleUploadingChange} />
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="sellerName">Seller Name (optional)</Label>
        <Input
          id="sellerName"
          value={form.sellerName}
          onChange={(e) => setForm({ ...form, sellerName: e.target.value })}
          placeholder="e.g. Acme Holdings LLC"
          data-testid="input-seller-name"
        />
        <p className="text-xs text-gray-500 mt-1">
          Use the legal name (LLC, trust, etc.) you want shown to buyers. Leave blank to display your account name.
        </p>
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v as SaleListingStatus })}>
          <SelectTrigger id="status" data-testid="select-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="under_contract">Under Contract</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <p className="text-xs text-gray-500">
        Buyers will reach out through PadPulse's built-in messaging — no need to share your email or phone publicly.
      </p>

      {error && <p className="text-sm text-red-600" data-testid="text-form-error">{error}</p>}

      {uploading && (
        <p className="text-sm text-gray-500 flex items-center gap-1.5" data-testid="text-uploading-hint">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Photos are still uploading — please wait before saving or closing.
        </p>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="button-cancel-listing">Cancel</Button>
        <Button type="submit" disabled={submitting || uploading} data-testid="button-submit-listing">
          {submitting ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
          ) : uploading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading photos…</>
          ) : (
            submitLabel
          )}
        </Button>
      </DialogFooter>
    </form>
  );
}

function ListingCard({
  listing, owned, onEdit, onDelete, onOpen, inquiryCount, unreadCount, copiedPropertyId,
}: {
  listing: MarketplaceListing | (SaleListing & Partial<MarketplaceListing>);
  owned: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onOpen: () => void;
  inquiryCount?: number;
  unreadCount?: number;
  copiedPropertyId?: number | null;
}) {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const cover = listing.photos?.[0];
  const status = listing.status as SaleListingStatus;

  const listingAlreadyCopied = !owned && copiedPropertyId != null;

  const listingCopyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/underwriting/copy-from-listing", { sourceType: "marketplace", sourceListingId: listing.id });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting/copy-check-batch"] });
      navigate(`/app/underwriting/${data.propertyId}`);
    },
    onError: () => toast({ title: "Could not copy to underwriting", variant: "destructive" }),
  });

  return (
    <div
      className="border border-gray-200 rounded-xl overflow-hidden bg-white hover:shadow-lg transition-shadow flex flex-col cursor-pointer"
      data-testid={`listing-card-${listing.id}`}
      onClick={onOpen}
      role="button"
    >
      <div className="relative bg-gray-100 h-48 flex items-center justify-center">
        {cover ? (
          <img src={cover} alt={listing.propertyName || "Property"} className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="h-10 w-10 text-gray-300" />
        )}
        <div className="absolute top-3 left-3">
          <Badge className={`${STATUS_COLOR[status] || STATUS_COLOR.active} border`} data-testid={`badge-status-${listing.id}`}>
            {STATUS_LABEL[status] || status}
          </Badge>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold text-gray-900 text-base mb-1" data-testid={`listing-name-${listing.id}`}>
          {listing.propertyName || `Listing #${listing.id}`}
        </h3>
        {listing.propertyAddress && (
          <p className="text-sm text-gray-500 mb-3 flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" /> {listing.propertyAddress}
          </p>
        )}

        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide">Asking Price</p>
            <p className="text-xl font-bold text-gray-900" data-testid={`listing-price-${listing.id}`}>
              {formatCurrency(listing.askingPrice)}
            </p>
          </div>
          {(listing.sellerName || (owned && (listing as SaleListing).sellerName)) && (
            <div className="text-right">
              <p className="text-xs text-gray-400 uppercase tracking-wide">Seller</p>
              <p className="text-sm font-medium text-gray-700 flex items-center gap-1 justify-end" data-testid={`listing-seller-${listing.id}`}>
                <User className="h-3.5 w-3.5" /> {listing.sellerName}
              </p>
            </div>
          )}
        </div>

        {listing.description && (
          <p className="text-sm text-gray-600 line-clamp-3 mb-3" data-testid={`listing-description-${listing.id}`}>
            {listing.description}
          </p>
        )}

        <div className="border-t border-gray-100 pt-3 mt-auto flex items-center justify-between gap-2">
          {owned ? (
            <>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-testid={`listing-inquiries-${listing.id}`}>
                <MessageSquare className="h-3.5 w-3.5" />
                <span>{inquiryCount || 0} {inquiryCount === 1 ? "inquiry" : "inquiries"}</span>
                {unreadCount ? (
                  <Badge className="bg-blue-600 text-white border-0">{unreadCount} new</Badge>
                ) : null}
              </div>
              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="outline" onClick={onEdit} data-testid={`button-edit-${listing.id}`}>
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" data-testid={`button-delete-${listing.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this listing?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently remove the listing from the marketplace. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid={`button-cancel-delete-${listing.id}`}>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={onDelete} data-testid={`button-confirm-delete-${listing.id}`}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 w-full justify-between" onClick={(e) => e.stopPropagation()}>
              <Button size="sm" variant="outline" onClick={onOpen} data-testid={`button-view-${listing.id}`}>View listing</Button>
              <Button
                size="sm"
                variant={listingAlreadyCopied ? "default" : "outline"}
                onClick={() => {
                  if (listingAlreadyCopied && copiedPropertyId) {
                    navigate(`/app/underwriting/${copiedPropertyId}`);
                  } else {
                    listingCopyMutation.mutate();
                  }
                }}
                disabled={listingCopyMutation.isPending}
                data-testid={`button-underwrite-myself-${listing.id}`}
              >
                {listingCopyMutation.isPending ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Copying...</>
                ) : listingAlreadyCopied ? (
                  "Review in Underwriting"
                ) : (
                  "Underwrite myself"
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <Skeleton className="w-full h-48" />
      <div className="p-4 space-y-3">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-8 w-32" />
      </div>
    </div>
  );
}

function EmptyState({ title, description, action }: { title: string; description: string; action?: React.ReactNode }) {
  return (
    <div className="col-span-full text-center py-16 text-gray-500" data-testid="empty-state">
      <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <Tag className="h-8 w-8 text-gray-400" />
      </div>
      <p className="text-lg font-medium text-gray-700">{title}</p>
      <p className="text-sm mt-1">{description}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

type SaleMessage = {
  id: number;
  inquiryId: number;
  senderId: number;
  content: string;
  readAt: string | null;
  createdAt: string;
};

/**
 * Gated wrapper around the listing create/edit dialog. Per the
 * "long-running dialogs gate dismissal on in-flight state" convention
 * (docs/dialog-dismissal-convention.md), every dismissal path is blocked
 * while work is in flight — the submit mutation OR an in-progress photo
 * upload — so a stray backdrop click / Escape / focus shift never silently
 * drops the user's photos or their submit.
 */
export function SellListingDialog({
  open,
  onOpenChange,
  inFlight,
  title,
  trigger,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inFlight: boolean;
  title: string;
  trigger?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [dismissBlocked, setDismissBlocked] = useState(false);

  useEffect(() => {
    if (!inFlight) setDismissBlocked(false);
  }, [inFlight]);

  const blockDismiss = (e: { preventDefault: () => void }) => {
    if (inFlight) {
      e.preventDefault();
      setDismissBlocked(true);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && inFlight) {
          setDismissBlocked(true);
          return;
        }
        onOpenChange(next);
      }}
    >
      {trigger}
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        onInteractOutside={blockDismiss}
        onEscapeKeyDown={blockDismiss}
        onPointerDownOutside={blockDismiss}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {dismissBlocked && (
          <p
            className="text-sm text-amber-600 flex items-center gap-1.5"
            role="status"
            data-testid="text-dismiss-blocked"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Please wait — this is still finishing. The form will stay open until it's done.
          </p>
        )}
        {children}
      </DialogContent>
    </Dialog>
  );
}

export default function AppSell() {
  const queryClient = useQueryClient();
  usePageTitle("Sell");
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<SaleListing | null>(null);
  const [createUploading, setCreateUploading] = useState(false);
  const [editUploading, setEditUploading] = useState(false);

  const [activeTab, setActiveTab] = useTabQueryParam("tab", "mine", {
    tabs: ["mine", "inquiries"],
  });
  const initialInquiryId = useMemo(() => {
    if (typeof window === "undefined") return null;
    const u = new URL(window.location.href);
    const v = u.searchParams.get("inquiry");
    return v ? parseInt(v, 10) : null;
  }, []);

  const { data: meResp } = useQuery<{ user: { id: number; email: string; name: string | null; isAdmin?: boolean } }>({
    queryKey: ["/api/app/auth/me"],
  });
  const currentUserId = meResp?.user?.id ?? null;

  const { data: properties } = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const { data: myListings, isLoading: loadingMine } = useQuery<SaleListing[]>({
    queryKey: ["/api/app/sell/listings"],
  });
  const { data: inquiries } = useQuery<InquirySummary[]>({
    queryKey: ["/api/app/sell/inquiries"],
  });

  const propertyMap = new Map((properties || []).map((p) => [p.id, p]));
  const sellableProperties = (properties || []).filter((p) => p.status === "active");

  const inquiryStats = useMemo(() => {
    const stats: Record<number, { total: number; unread: number }> = {};
    (inquiries || []).forEach((inq) => {
      const cur = stats[inq.listingId] || { total: 0, unread: 0 };
      cur.total += 1;
      cur.unread += inq.unreadCount || 0;
      stats[inq.listingId] = cur;
    });
    return stats;
  }, [inquiries]);

  const createMutation = useMutation({
    mutationFn: async (data: FormState) => {
      const body = {
        propertyId: parseInt(data.propertyId, 10),
        askingPrice: parseFloat(data.askingPrice),
        description: data.description || null,
        photos: data.photos,
        status: data.status,
        sellerName: data.sellerName || null,
      };
      const res = await apiRequest("POST", "/api/app/sell/listings", body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/sell/listings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/sell/marketplace"] });
      toast({ title: "Listing created", description: "Your property is now listed in the marketplace." });
      setCreateOpen(false);
    },
    onError: (err: any) => {
      toast({ title: "Could not create listing", description: parseErrorMessage(err, "Something went wrong"), variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: FormState }) => {
      const body = {
        propertyId: parseInt(data.propertyId, 10),
        askingPrice: parseFloat(data.askingPrice),
        description: data.description || null,
        photos: data.photos,
        status: data.status,
        sellerName: data.sellerName || null,
      };
      const res = await apiRequest("PATCH", `/api/app/sell/listings/${id}`, body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/sell/listings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/sell/marketplace"] });
      toast({ title: "Listing updated" });
      setEditing(null);
    },
    onError: (err: any) => {
      toast({ title: "Could not update listing", description: parseErrorMessage(err, "Something went wrong"), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/app/sell/listings/${id}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/sell/listings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/sell/marketplace"] });
      toast({ title: "Listing deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Could not delete listing", description: parseErrorMessage(err, "Something went wrong"), variant: "destructive" });
    },
  });

  const enrichOwn = (listing: SaleListing): MarketplaceListing => {
    const prop = propertyMap.get(listing.propertyId);
    return {
      ...listing,
      propertyName: prop?.name || `Property #${listing.propertyId}`,
      propertyAddress: prop?.address ?? null,
      latitude: prop?.latitude ?? null,
      longitude: prop?.longitude ?? null,
      sellerName: listing.sellerName,
    };
  };

  const editProperties = useMemo(() => {
    if (!editing) return sellableProperties;
    if (sellableProperties.some((p) => p.id === editing.propertyId)) return sellableProperties;
    const current = propertyMap.get(editing.propertyId);
    return current ? [current, ...sellableProperties] : sellableProperties;
  }, [editing, sellableProperties, propertyMap]);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <ViewOnlyBanner area="properties" />
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Tag className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="text-sell-heading" data-tour="sell-heading">Sell</h1>
              <p className="text-sm text-gray-500">List your properties for sale and manage buyer inquiries.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SellListingDialog
              open={createOpen}
              onOpenChange={setCreateOpen}
              inFlight={createMutation.isPending || createUploading}
              title="List a property for sale"
              trigger={(
                <DialogTrigger asChild>
                  <Button data-testid="button-new-listing" data-tour="sell-add">
                    <Plus className="h-4 w-4 mr-2" /> New Listing
                  </Button>
                </DialogTrigger>
              )}
            >
              <ListingForm
                key={createOpen ? "create-open" : "create-closed"}
                initial={EMPTY_FORM}
                properties={sellableProperties}
                mode="create"
                onSubmit={(data) => createMutation.mutate(data)}
                onCancel={() => setCreateOpen(false)}
                submitting={createMutation.isPending}
                submitLabel="Create listing"
                onUploadingChange={setCreateUploading}
              />
            </SellListingDialog>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="sell" />
              <GuideButton pageKey="sell" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="sell" />
        </div>

        <div data-tour="sell-list">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="mine" data-testid="tab-my-listings" data-tour="sell-tab-mine">
              <Tag className="h-4 w-4 mr-1.5" /> My Listings
            </TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-my-inquiries" data-tour="sell-tab-inquiries">
              <Inbox className="h-4 w-4 mr-1.5" /> My Inquiries
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inquiries">
            <div data-tour="sell-inquiries">
            {currentUserId ? (
              <BuyerInquiriesView currentUserId={currentUserId} initialInquiryId={initialInquiryId} />
            ) : (
              <div className="text-sm text-gray-500">Loading...</div>
            )}
            </div>
          </TabsContent>

          <TabsContent value="mine">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-tour="sell-mine-grid">
              {loadingMine ? (
                Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)
              ) : myListings && myListings.length > 0 ? (
                myListings.map((listing) => {
                  const stats = inquiryStats[listing.id];
                  return (
                    <ListingCard
                      key={listing.id}
                      listing={enrichOwn(listing)}
                      owned
                      onEdit={() => setEditing(listing)}
                      onDelete={() => deleteMutation.mutate(listing.id)}
                      onOpen={() => navigate(`/app/sell/${listing.id}`)}
                      inquiryCount={stats?.total || 0}
                      unreadCount={stats?.unread || 0}
                    />
                  );
                })
              ) : (
                <EmptyState
                  title="You haven't listed anything yet"
                  description="Create your first listing to start connecting with buyers."
                  action={(
                    <Button onClick={() => setCreateOpen(true)} data-testid="button-empty-create-mine">
                      <Plus className="h-4 w-4 mr-2" /> Create a listing
                    </Button>
                  )}
                />
              )}
            </div>
          </TabsContent>
        </Tabs>
        </div>

        <SellListingDialog
          open={editing !== null}
          onOpenChange={(open) => { if (!open) setEditing(null); }}
          inFlight={updateMutation.isPending || editUploading}
          title="Edit listing"
        >
          {editing && (
            <ListingForm
              initial={{
                propertyId: String(editing.propertyId),
                askingPrice: String(editing.askingPrice),
                description: editing.description || "",
                photos: editing.photos || [],
                status: editing.status as SaleListingStatus,
                sellerName: editing.sellerName || "",
              }}
              properties={editProperties}
              mode="edit"
              onSubmit={(data) => updateMutation.mutate({ id: editing.id, data })}
              onCancel={() => setEditing(null)}
              submitting={updateMutation.isPending}
              submitLabel="Save changes"
              onUploadingChange={setEditUploading}
            />
          )}
        </SellListingDialog>
      </div>
    </AppLayout>
  );
}
